import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectors } from "../../../store/selectors/shipBuilderSelectors";
import { selectTotalUsedCargoTonnage } from "../../../store/selectors/derivedSelectors";
import { setSupplyUnits } from "../../../store/features/shipBuilderSlice";
import { formatCurrency } from "../../../utils/formatting";

import { selectIsBreakaway } from "../../../store/selectors/breakawayHullSelectors";
import { AMMO_TYPES } from "../../../constants/ship";
import { AmmunitionStorage } from "../../../types/ship";
import BreakawayAmmunitionManager from "../components/weapons/BreakawayAmmunitionManager";

interface SectionCargoAssignment {
  sectionId: string;
  sectionName: string;
  sectionSize: number;
  supplyUnits: number;
  ammunition: AmmunitionStorage[];
}

const BreakawayCargoStep: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get ship data
  const shipSize = useAppSelector(selectors.selectShipSize);
  const usedCargo = useAppSelector(selectTotalUsedCargoTonnage);
  const availableCargo = shipSize - usedCargo;

  // Get current supply and ammunition data
  const supplyUnits = useAppSelector(selectors.selectSupplyUnits);
  const ammunitionStorage = useAppSelector((state) => state.shipBuilder.ammunitionStorage || []);

  // Breakaway hulls detection
  const isBreakaway = useAppSelector(selectIsBreakaway);
  const breakawayHull = useAppSelector((state) => state.breakawayHull);
  const hasBreakawayHulls = isBreakaway || breakawayHull?.isBreakaway;

  const [sectionAssignments, setSectionAssignments] = useState<SectionCargoAssignment[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Calculate supply requirements based on Traveller rules
  const dailyConsumption = Math.ceil(shipSize / 100); // SU per day
  const recommendedSupplyTons = Math.ceil((dailyConsumption * 100) / 100); // 100 days worth
  // Allow supply units even when cargo space is limited - they can be stored in other areas
  const maximumSupplyTons = Math.max(100, Math.floor(availableCargo)); // Minimum 100 tons to allow input
  const supplyUnitsPerTon = 100; // 100 SU = 1 ton

  // Initialize assignments from existing supply and ammunition configuration
  useEffect(() => {
    if (breakawayHull?.sections) {
      const newAssignments: SectionCargoAssignment[] = [];

      // Distribute supplies and ammunition proportionally across sections based on section size
      const totalShipSize = breakawayHull.sections.reduce((total, section) => total + section.size, 0);

      breakawayHull.sections.forEach(section => {
        const sectionRatio = section.size / totalShipSize;
        
        // Distribute supply units proportionally
        const proportionalSupplyUnits = Math.round(supplyUnits * sectionRatio);
        
        // Distribute ammunition proportionally
        const proportionalAmmunition = ammunitionStorage.map(ammo => ({
          ...ammo,
          id: `${ammo.id}-section-${section.id}`,
          quantity: Math.round(ammo.quantity * sectionRatio)
        }));

        newAssignments.push({
          sectionId: section.id,
          sectionName: section.name,
          sectionSize: section.size,
          supplyUnits: proportionalSupplyUnits,
          ammunition: proportionalAmmunition,
        });
      });

      setSectionAssignments(newAssignments);
    }
  }, [breakawayHull?.sections, supplyUnits, ammunitionStorage]);

  const handleSupplyUnitsChange = (sectionId: string, units: number) => {
    setSectionAssignments(prev => 
      prev.map(assignment => 
        assignment.sectionId === sectionId
          ? { ...assignment, supplyUnits: Math.max(0, units) }
          : assignment
      )
    );
    setErrors(prev => ({ ...prev, [`${sectionId}-supply`]: '' }));
  };

  const handleAddAmmunition = (sectionId: string, ammoType: string, quantity: number) => {
    const ammoData = AMMO_TYPES[ammoType as keyof typeof AMMO_TYPES];
    if (!ammoData) return;

    const newAmmunition: AmmunitionStorage = {
      id: `${ammoType}-section-${sectionId}-${Date.now()}`,
      ammoType: ammoType,
      quantity: quantity,
      name: ammoData.name,
      cost: ammoData.cost,
      tonsPerReload: ammoData.tonsPerReload,
      traits: [...ammoData.traits],
      damage: ammoData.damage,
      tl: ammoData.tl,
      perReload: ammoData.perReload,
    };

    setSectionAssignments(prev => 
      prev.map(assignment => 
        assignment.sectionId === sectionId
          ? { ...assignment, ammunition: [...assignment.ammunition, newAmmunition] }
          : assignment
      )
    );
  };

  const handleRemoveAmmunition = (sectionId: string, ammoId: string) => {
    setSectionAssignments(prev => 
      prev.map(assignment => 
        assignment.sectionId === sectionId
          ? { ...assignment, ammunition: assignment.ammunition.filter(ammo => ammo.id !== ammoId) }
          : assignment
      )
    );
  };

  const handleUpdateAmmunitionQuantity = (sectionId: string, ammoId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      handleRemoveAmmunition(sectionId, ammoId);
      return;
    }

    setSectionAssignments(prev => 
      prev.map(assignment => 
        assignment.sectionId === sectionId
          ? {
              ...assignment,
              ammunition: assignment.ammunition.map(ammo => 
                ammo.id === ammoId ? { ...ammo, quantity: newQuantity } : ammo
              )
            }
          : assignment
      )
    );
  };

  const getTotalAssignedSupplyUnits = () => {
    return sectionAssignments.reduce((sum, assignment) => sum + assignment.supplyUnits, 0);
  };



  const validateAssignments = () => {
    const newErrors: Record<string, string> = {};
    
    // Check if total supply units match original
    const totalAssignedSupplyUnits = getTotalAssignedSupplyUnits();
    if (totalAssignedSupplyUnits !== supplyUnits) {
      newErrors['supply'] = `Total assigned supply units (${totalAssignedSupplyUnits}) must equal original total (${supplyUnits})`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateAssignments()) {
      return;
    }

    // Update the main supply units with the total
    const totalAssignedSupplyUnits = getTotalAssignedSupplyUnits();
    dispatch(setSupplyUnits(totalAssignedSupplyUnits));

    // Note: Ammunition storage would need to be updated in the main store
    // This would require additional Redux actions for breakaway ammunition management
  };

  const calculateSectionTotals = (assignment: SectionCargoAssignment) => {
    const supplyCost = (assignment.supplyUnits / 100) * 1000; // 1000 Cr per ton
    const ammunitionCost = assignment.ammunition.reduce((sum, ammo) => sum + (ammo.cost * ammo.quantity), 0);
    
    const supplyTons = assignment.supplyUnits / 100;
    const ammunitionTons = assignment.ammunition.reduce((sum, ammo) => sum + (ammo.tonsPerReload * ammo.quantity), 0);

    return {
      cost: supplyCost + ammunitionCost,
      tons: supplyTons + ammunitionTons,
      supplyTons: supplyTons,
      ammunitionTons: ammunitionTons,
    };
  };

  const calculateShipTotals = () => {
    return sectionAssignments.reduce((totals, assignment) => {
      const sectionTotals = calculateSectionTotals(assignment);
      return {
        cost: totals.cost + sectionTotals.cost,
        tons: totals.tons + sectionTotals.tons,
        supplyTons: totals.supplyTons + sectionTotals.supplyTons,
        ammunitionTons: totals.ammunitionTons + sectionTotals.ammunitionTons,
      };
    }, { cost: 0, tons: 0, supplyTons: 0, ammunitionTons: 0 });
  };

  if (!hasBreakawayHulls || !breakawayHull?.sections) {
    return (
      <div className="w-full pb-32">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
            Breakaway Cargo Configuration
          </h2>
          <p className="text-gray-600 dark:text-gray-300">No breakaway hull configuration detected.</p>
        </div>
      </div>
    );
  }

  const shipTotals = calculateShipTotals();

  return (
    <div className="w-full pb-32">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
          Breakaway Cargo Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Allocate supply units and ammunition storage to specific breakaway hull sections
        </p>
      </div>

      {/* Breakaway Hulls Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
              Breakaway Hulls Configuration
            </h3>
            <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
              {breakawayHull?.sections?.length || 0} section{(breakawayHull?.sections?.length || 0) > 1 ? 's' : ''} 
              ({breakawayHull?.sections?.map(s => `${s.name}: ${s.size} tons`).join(", ") || ''})
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>Note:</strong> Allocate supplies and ammunition to specific sections. Each section can have all, some, or none of the available supplies and ammunition.
            </p>
          </div>
        </div>
      </div>

      {/* Supply Requirements Information */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
        <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
          Supply Requirements
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-600 dark:text-blue-400">Ship Size:</span>
            <div className="text-blue-900 dark:text-blue-100">
              {shipSize.toLocaleString()} tons
            </div>
          </div>
          <div>
            <span className="font-medium text-blue-600 dark:text-blue-400">
              Daily Consumption:
            </span>
            <div className="text-blue-900 dark:text-blue-100">{dailyConsumption} SU/day</div>
          </div>
          <div>
            <span className="font-medium text-blue-600 dark:text-blue-400">
              Recommended Supply:
            </span>
            <div className="text-blue-900 dark:text-blue-100">{recommendedSupplyTons} tons</div>
          </div>
          <div>
            <span className="font-medium text-blue-600 dark:text-blue-400">Available Cargo:</span>
            <div className="text-blue-900 dark:text-blue-100">
              {availableCargo.toLocaleString()} tons
            </div>
          </div>
        </div>
      </div>

      {/* Section Assignments */}
      <div className="space-y-6">
        {sectionAssignments.map((assignment) => {
          const sectionTotals = calculateSectionTotals(assignment);
          
          return (
            <div key={assignment.sectionId} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                  {assignment.sectionName} Section
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {assignment.sectionSize} tons
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Supply Units - Compact */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-3">
                    Supply Units
                  </h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Supply Units (SU)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={maximumSupplyTons * supplyUnitsPerTon}
                        step="100"
                        value={assignment.supplyUnits}
                        onChange={(e) => handleSupplyUnitsChange(assignment.sectionId, parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-gray-100"
                      />
                      {availableCargo <= 0 && (
                        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                          ⚠️ Limited cargo space. Supply units may need to be stored in other areas.
                        </p>
                      )}
                    </div>
                    
                    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                      <p>{(assignment.supplyUnits / 100).toFixed(1)} tons</p>
                      <p>{assignment.supplyUnits > 0 ? Math.ceil(assignment.supplyUnits / dailyConsumption) : 0} days of supply</p>
                      <p className="font-medium">Cost: {formatCurrency((assignment.supplyUnits / 100) * 1000)}</p>
                    </div>
                  </div>
                </div>

                {/* Ammunition Storage - Expanded */}
                <div className="lg:col-span-2 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-3">
                    Ammunition Storage
                  </h4>
                  
                  <BreakawayAmmunitionManager
                    sectionId={assignment.sectionId}
                    sectionName={assignment.sectionName}
                    ammunition={assignment.ammunition}
                    onAddAmmunition={handleAddAmmunition}
                    onRemoveAmmunition={handleRemoveAmmunition}
                    onUpdateAmmunitionQuantity={handleUpdateAmmunitionQuantity}
                  />
                </div>
              </div>

              {/* Section Totals */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Section Cost:</span>
                    <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{formatCurrency(sectionTotals.cost)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Section Weight:</span>
                    <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{sectionTotals.tons.toFixed(1)} tons</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-300">Total Items:</span>
                    <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{assignment.ammunition.length} types</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Validation Errors */}
      {Object.keys(errors).length > 0 && (
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
          <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">Validation Errors</h4>
          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
            {Object.entries(errors).map(([field, error]) => (
              <li key={field}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Ship Totals */}
      <div className="mt-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Overall Ship Totals</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Cost:</span>
            <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{formatCurrency(shipTotals.cost)}</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Total Weight:</span>
            <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{shipTotals.tons.toFixed(1)} tons</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Supply Weight:</span>
            <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{shipTotals.supplyTons.toFixed(1)} tons</span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-300">Ammunition Weight:</span>
            <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{shipTotals.ammunitionTons.toFixed(1)} tons</span>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-300">Total Supply Units:</span>
              <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">{getTotalAssignedSupplyUnits().toLocaleString()} SU</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-300">Days of Supply:</span>
              <span className="font-semibold ml-2 text-gray-900 dark:text-gray-100">
                {getTotalAssignedSupplyUnits() > 0 ? Math.ceil(getTotalAssignedSupplyUnits() / dailyConsumption) : 0} days
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={Object.keys(errors).length > 0}
          className={`px-6 py-2 rounded-md font-medium ${
            Object.keys(errors).length > 0
              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
          }`}
        >
          Save Configuration
        </button>
      </div>
    </div>
  );
};

export default BreakawayCargoStep;
